<template>
    <div v-if="isCurrentStep">
        <NextStep title="Create a Resume That Gets Interviews">
            <div>
                <p>Your resume has <span class="font-bold">ONE</span> job: getting you interviews.</p>
                <br />
                <p>
                    Recruiters spend just 6 seconds reviewing each resume before deciding whether to continue or move
                    on.
                </p>
                <br />
                <p>A high-scoring resume makes those seconds count.</p>
                <br />
                <p class="font-bold">How to complete this stage:</p>
                <ol class="list-decimal pl-4">
                    <li>Set your primary resume on ResumAI</li>
                    <li>Get your primary resume score to 75+ to ensure your resume is interview-ready</li>
                </ol>
            </div>
        </NextStep>
    </div>
    <div v-else-if="isStepPassed">
        <NextStep title="Review">
            <div>
                <p>Your resume now scores 75+.</p>
                <br />
                <p>This effectively showcases your achievements in a recruiter-friendly format.</p>
                <br />
                <p>It's optimized to pass the critical 6-second review and generate interviews for your target role.</p>
            </div>
        </NextStep>
    </div>
    <div v-else>
        <NextStep title="Preview">
            <p>Create a resume that passes the 6-second test.</p>
            <br />
            <p>Recruiters make quick decisions, so your resume needs clear achievements with measurable results.</p>
            <br />
            <p>Your goal is to get a 75+ score that indicates interview-readiness.</p>
        </NextStep>
    </div>

    <button
        class="bg-won-green w-full text-white py-1.5 px-3 rounded text-sm"
        type="button"
        @click="() => emit('open-feature')"
    >
        Improve Your Resume Now →
    </button>

    <SidebarCard>
        <span class="text-sm font-semibold text-won-blue text-center">Your Primary Resume score</span>
        <ScoreCircle :progress="primaryResumeScore" />
        <div @click="openResumeHandler" class="flex justify-center items-center space-x-0.5 cursor-pointer">
            <span class="text-won-blue text-xs font-semibold">{{
                primaryResume ? "View Resume" : "View Resumes"
            }}</span>
            <ArrowRightIcon class="size-4 text-won-green" />
        </div>
    </SidebarCard>
</template>

<script setup>
import { onMounted } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";

import { useJobSearchPlanStore } from "../../../store/job-search-plan.store";
import { usePrimaryResumeStore } from "../../../store/primary-resume.store";
import { useResumesStore } from "../../../store/resumes.store";

import SidebarCard from "../sidebar-card.vue";
import NextStep from "../next-step.vue";
import ScoreCircle from "../../resume-score/score-circle.vue";

import { ArrowRightIcon } from "@heroicons/vue/20/solid";

const emit = defineEmits(["open-feature"]);
const props = defineProps({
    isStepPassed: {
        type: Boolean,
        default: false,
        required: true,
    },
    isCurrentStep: {
        type: Boolean,
        default: false,
        required: true,
    },
});

const jobSearchPlanStore = useJobSearchPlanStore();
const primaryResumeStore = usePrimaryResumeStore();
const resumeStore = useResumesStore();

const router = useRouter();

const { primaryResumeScore } = storeToRefs(jobSearchPlanStore);
const { primaryResume } = storeToRefs(primaryResumeStore);
const { hasResumesLoaded } = storeToRefs(resumeStore);

const openResumeHandler = () => {
    if (primaryResume.value) {
        router.push({ name: "resumes.edit", params: { id: primaryResume.value.uuid } });
    } else {
        router.push("resumes");
    }
};

onMounted(() => {
    if (!hasResumesLoaded?.value) {
        resumeStore.fetchResumes();
    }
});
</script>
